import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Input, Spin, Typography } from "antd";
import {
  CalendarOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GlobalOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ShoppingOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./QRPublic.css";

const { Title, Paragraph } = Typography;

const parsePayload = (payloadData) => {
  if (!payloadData) {
    return {};
  }

  try {
    return typeof payloadData === "string" ? JSON.parse(payloadData) : payloadData;
  } catch {
    return {};
  }
};

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

const sanitizeHtml = (html = "") =>
  html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

const getDeviceType = () => (/Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop");

const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Edg")) return "Edge";
  return "Unknown";
};

const getSocialInitial = (platform = "S") => platform.trim().charAt(0).toUpperCase() || "S";

const PRODUCT_PLATFORMS = ["Amazon", "Flipkart", "Myntra", "Shopify", "Meesho", "Custom Store"];

const getProductPlatform = (platform = "") =>
  PRODUCT_PLATFORMS.includes(platform) ? platform : "";

const getFileName = (url = "") => {
  try {
    return decodeURIComponent(url.split("/").pop()?.split("?")[0] || "File");
  } catch {
    return "File";
  }
};

const getFileExtension = (url = "") => {
  const fileName = getFileName(url);
  return fileName.includes(".") ? fileName.split(".").pop().toUpperCase() : "FILE";
};

const isImageFile = (url = "") => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url.split("?")[0]);

const getFirstUrl = (...values) => {
  for (const value of values) {
    if (!value) continue;
    if (Array.isArray(value)) {
      const match = value
        .map((item) => getFirstUrl(item))
        .find(Boolean);
      if (match) return match;
      continue;
    }
    if (typeof value === "object") {
      const match =
        value.fileUrl ||
        value.url ||
        value.imageUrl ||
        value.pictureUrl ||
        value.attachmentUrl ||
        value.messageImageUrl ||
        value.photoUrl ||
        value.s3Url ||
        value.file?.fileUrl ||
        value.file?.url ||
        value.image?.fileUrl ||
        value.image?.url ||
        value.picture?.fileUrl ||
        value.picture?.url ||
        "";
      if (match) return match;
      continue;
    }
    return value;
  }
  return "";
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "VC";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
};

const QRPublic = () => {
  const { uniqueId } = useParams();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [qrData, setQrData] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const trackedRef = useRef(false);

  const fetchQR = useCallback(async () => {
    setLoading(true);
    setError("");
    setPasswordError("");
    trackedRef.current = false;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/WebsiteQr/${uniqueId}`
      );

      if (!response.data?.error) {
        setQrData(response.data.data || {});
      } else {
        setError(response.data?.message || "Unable to load this QR code.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "This QR code could not be found.");
    } finally {
      setLoading(false);
    }
  }, [uniqueId]);

  const trackScan = useCallback(async () => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/Qr/${uniqueId}/track-scan`, {
        device: getDeviceType(),
        browser: getBrowserName(),
        location: "",
      });
      if (!response.data?.error && response.data?.data) {
        setQrData((prev) => ({ ...(prev || {}), ...response.data.data }));
      }
    } catch {
      // Scan tracking must never block content rendering.
    }
  }, [uniqueId]);

  useEffect(() => {
    fetchQR();
  }, [fetchQR]);

  useEffect(() => {
    if (qrData && !qrData.isPasswordProtected) {
      trackScan();
    }
  }, [qrData, trackScan]);

  const verifyPassword = async () => {
    if (!password.trim()) {
      setPasswordError("Please enter the QR password.");
      return;
    }

    setVerifying(true);
    setPasswordError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/WebsiteQr/${uniqueId}/verify-password`,
        { password }
      );

      if (!response.data?.error) {
        setQrData((prev) => ({ ...(prev || {}), ...(response.data.data || {}) }));
        setPassword("");
        trackedRef.current = false;
        await trackScan();
      } else {
        setPasswordError(response.data?.message || "Invalid QR password.");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        setPasswordError("Invalid QR password.");
      } else {
        setPasswordError(err?.response?.data?.message || "Unable to verify password.");
      }
    } finally {
      setVerifying(false);
    }
  };

  const normalizeExternalUrl = (url = "") => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return "";
    if (/^(https?:|mailto:|tel:)/i.test(trimmedUrl)) return trimmedUrl;
    return `https://${trimmedUrl}`;
  };

  const openUrl = (url) => {
    if (!url) return;
    window.open(normalizeExternalUrl(url), "_blank", "noopener,noreferrer");
  };

  const renderContent = () => {
    const payload = parsePayload(qrData?.payloadData);
    const qrType = qrData?.qrType;
    const link = qrData?.link || payload.url || payload.websiteUrl || payload.mapsUrl || payload.socialUrl || payload.productUrl;
    const text = qrData?.qrText || payload.text || payload.description || "";

    if (qrType === "Location") {
      const venue = payload.venueName || qrData?.title || "Location";
      const address = payload.address || "Open this location in maps";

      return (
        <div className="qrp-location">
          <div className="qrp-location-hero">
            <div className="qrp-location-pin"><EnvironmentOutlined /></div>
            <div className="qrp-location-map-lines" />
          </div>
          <div className="qrp-location-body">
            <span className="qrp-type-pill">Location QR</span>
            <Title level={2}>{venue}</Title>
            <Paragraph className="qrp-location-address">{address}</Paragraph>
            <Button type="primary" size="large" onClick={() => openUrl(link)} disabled={!link}>
              Open Google Maps
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Brand") {
      const companyName = payload.companyName || qrData?.title || "Brand";
      const website = qrData?.link || payload.websiteUrl || payload.url || link;

      return (
        <div className="qrp-brand">
          <div className="qrp-brand-hero">
            <div className="qrp-brand-glow" />
            <div className="qrp-brand-logo">
              {qrData.logoUrl ? (
                <img src={qrData.logoUrl} alt={companyName} />
              ) : (
                <GlobalOutlined />
              )}
            </div>
          </div>
          <div className="qrp-brand-body">
            <span className="qrp-type-pill">Brand QR</span>
            <Title level={2}>{companyName}</Title>
            <Paragraph className="qrp-brand-subtitle">{qrData?.title}</Paragraph>
            {website && <div className="qrp-brand-url"><GlobalOutlined /> {website}</div>}
            <Button type="primary" size="large" onClick={() => openUrl(website)} disabled={!website}>
              Visit Website
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Social") {
      const platform = payload.platform || "Social";
      const companyName = payload.companyName || qrData?.title || platform;
      const socialUrl = qrData?.link || payload.socialUrl || payload.url || link;

      return (
        <div className="qrp-social">
          <div className="qrp-social-hero">
            <div className="qrp-social-orbit one" />
            <div className="qrp-social-orbit two" />
            <div className="qrp-social-avatar">{getSocialInitial(platform)}</div>
          </div>
          <div className="qrp-social-body">
            <span className="qrp-type-pill">Social QR</span>
            <Title level={2}>{companyName}</Title>
            <div className="qrp-social-platform"><ShareAltOutlined /> {platform}</div>
            {socialUrl && <Paragraph className="qrp-social-url">{socialUrl}</Paragraph>}
            <Button type="primary" size="large" onClick={() => openUrl(socialUrl)} disabled={!socialUrl}>
              Open {platform}
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Product") {
      const productName = qrData?.title || "Product";
      const companyName = payload.companyName || "";
      const platform = getProductPlatform(payload.platform);
      const sku = payload.sku || "";
      const productUrl = qrData?.link || payload.productUrl || payload.url || link;

      return (
        <div className="qrp-product">
          <div className="qrp-product-hero">
            <div className="qrp-product-bag"><ShoppingOutlined /></div>
          </div>
          <div className="qrp-product-body">
            <span className="qrp-type-pill">Product QR</span>
            <Title level={2}>{productName}</Title>
            {companyName && <Paragraph className="qrp-product-company">{companyName}</Paragraph>}
            {(platform || sku) && (
              <div className="qrp-product-chips">
                {platform && <span>{platform}</span>}
                {sku && <span>SKU: {sku}</span>}
              </div>
            )}
            {productUrl && <div className="qrp-product-url"><GlobalOutlined /> {productUrl}</div>}
            <Button type="primary" size="large" onClick={() => openUrl(productUrl)} disabled={!productUrl}>
              View Product
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Website") {
      const websiteTitle = qrData?.title || payload.title || "Website";
      const websiteUrl = qrData?.link || payload.url || payload.link || payload.websiteUrl || link;
      const hostname = (() => {
        try {
          return new URL(websiteUrl).hostname.replace(/^www\./, "");
        } catch {
          return websiteUrl || "Website link";
        }
      })();

      return (
        <div className="qrp-website">
          <div className="qrp-website-hero">
            <div className="qrp-website-ring" />
            <div className="qrp-website-icon"><GlobalOutlined /></div>
          </div>
          <div className="qrp-website-body">
            <span className="qrp-type-pill">Website QR</span>
            <Title level={2}>{websiteTitle}</Title>
            <Paragraph className="qrp-website-domain">{hostname}</Paragraph>
            {websiteUrl && (
              <div className="qrp-website-url">
                <GlobalOutlined /> {normalizeExternalUrl(websiteUrl)}
              </div>
            )}
            <Button type="primary" size="large" onClick={() => openUrl(websiteUrl)} disabled={!websiteUrl}>
              Open Website
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Text") {
      const textTitle = qrData?.title || payload.title || "Text";
      const textContent = text || payload.text || "No content available.";

      return (
        <div className="qrp-text-card">
          <div className="qrp-text-hero">
            <div className="qrp-text-paper">
              <FileTextOutlined />
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="qrp-text-body">
            <span className="qrp-type-pill">Text QR</span>
            <Title level={2}>{textTitle}</Title>
            <Paragraph className="qrp-text-subtitle">Shared text content</Paragraph>
            <div
              className="qrp-text-document qrp-rich-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(textContent) }}
            />
          </div>
        </div>
      );
    }

    if (qrType === "File") {
      const fileUrl = qrData.fileUrl || payload.fileUrl;
      const fileName = getFileName(fileUrl);
      const fileExt = getFileExtension(fileUrl);

      return (
        <div className="qrp-file">
          <div className="qrp-file-hero">
            {isImageFile(fileUrl) ? (
              <img src={fileUrl} alt={fileName} />
            ) : (
              <div className="qrp-file-doc"><DownloadOutlined /><span>{fileExt}</span></div>
            )}
          </div>
          <div className="qrp-file-body">
            <span className="qrp-type-pill">File QR</span>
            <Title level={2}>{qrData?.title || "Shared File"}</Title>
            <div className="qrp-file-name">
              <DownloadOutlined />
              <span>{fileName}</span>
              <strong>{fileExt}</strong>
            </div>
            <Button type="primary" size="large" href={fileUrl} target="_blank" disabled={!fileUrl}>
              Open File
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "vCard") {
      const contactName = payload.name || qrData?.title || "Contact";

      return (
        <div className="qrp-vcard">
          <div className="qrp-vcard-hero">
            <div className="qrp-vcard-avatar">{getInitials(contactName)}</div>
          </div>
          <div className="qrp-vcard-body">
            <span className="qrp-type-pill">vCard QR</span>
            <Title level={2}>{contactName}</Title>
            {payload.designation && <Paragraph className="qrp-vcard-role">{payload.designation}</Paragraph>}
            {payload.companyName && <div className="qrp-vcard-company"><IdcardOutlined /> {payload.companyName}</div>}

            <div className="qrp-vcard-actions">
              {payload.mobile && <a href={`tel:${payload.mobile}`}><PhoneOutlined /> Call</a>}
              {payload.email && <a href={`mailto:${payload.email}`}><MailOutlined /> Email</a>}
              {payload.website && <a href={payload.website} target="_blank" rel="noopener noreferrer"><GlobalOutlined /> Website</a>}
            </div>

            <div className="qrp-contact-card">
              {payload.mobile && <div><strong>Mobile</strong><span>{payload.mobile}</span></div>}
              {payload.email && <div><strong>Email</strong><span>{payload.email}</span></div>}
              {payload.website && <div><strong>Website</strong><span>{payload.website}</span></div>}
              {payload.address && <div><strong>Address</strong><span>{payload.address}</span></div>}
            </div>
          </div>
        </div>
      );
    }

    if (qrType === "Message") {
      const subject = payload.subject || qrData?.title || "Message";
      const description = text || "No message available.";
      const eventDate = payload.eventDate || "";
      const messageImage = getFirstUrl(
        qrData.fileUrl,
        qrData.imageUrl,
        qrData.pictureUrl,
        qrData.attachmentUrl,
        qrData.messageImageUrl,
        qrData.photoUrl,
        qrData.s3Url,
        qrData.file,
        qrData.image,
        qrData.picture,
        qrData.files,
        qrData.attachments,
        qrData.media,
        payload.fileUrl,
        payload.imageUrl,
        payload.pictureUrl,
        payload.attachmentUrl,
        payload.messageImageUrl,
        payload.photoUrl,
        payload.file,
        payload.image,
        payload.picture,
        payload.files
      );

      return (
        <div className="qrp-message-card">
          <div className="qrp-message-hero">
            <div className="qrp-message-icon">
              <MessageOutlined />
            </div>
          </div>
          <div className="qrp-message-body">
            <span className="qrp-type-pill">Message QR</span>
            <Title level={2}>{subject}</Title>
            {eventDate && (
              <div className="qrp-message-date">
                <CalendarOutlined /> {eventDate}
              </div>
            )}
            {messageImage && (
              <div className="qrp-message-picture">
                <img src={messageImage} alt={subject} />
              </div>
            )}
            <div className="qrp-message-note">
              <span>Message</span>
              <div
                className="qrp-rich-content"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="qrp-content">
        <div className="qrp-content-icon"><MessageOutlined /></div>
        <Title level={3}>{payload.subject || (qrType === "Message" ? "Message" : "Text")}</Title>
        {stripHtml(text) ? (
          <div
            className="qrp-message qrp-rich-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
          />
        ) : (
          <Paragraph className="qrp-message">No content available.</Paragraph>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="qrp-page">
        <Card className="qrp-card center"><Spin size="large" /><Paragraph>Loading QR...</Paragraph></Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qrp-page">
        <Card className="qrp-card center">
          <Title level={3}>QR unavailable</Title>
          <Paragraph>{error}</Paragraph>
          <Button icon={<ReloadOutlined />} onClick={fetchQR}>Retry</Button>
        </Card>
      </div>
    );
  }

  if (qrData?.isPasswordProtected && !qrData?.link && !qrData?.qrText && !qrData?.payloadData && !qrData?.fileUrl) {
    return (
      <div className="qrp-page">
        <Card className="qrp-card">
          <div className="qrp-protected">
            <div className={`qrp-protected-visual ${qrData.qrType === "Location" ? "location" : ""} ${qrData.qrType === "Brand" ? "brand" : ""} ${qrData.qrType === "Social" ? "social" : ""} ${qrData.qrType === "File" ? "file" : ""} ${qrData.qrType === "Product" ? "product" : ""}`}>
              {qrData.qrType === "Brand" && qrData.logoUrl ? (
                <img className="brand-logo" src={qrData.logoUrl} alt={qrData.title || "Brand QR"} />
              ) : qrData.qrType === "File" && isImageFile(qrData.fileUrl || parsePayload(qrData.payloadData).fileUrl) ? (
                <img className="file-preview" src={qrData.fileUrl || parsePayload(qrData.payloadData).fileUrl} alt={qrData.title || "File QR"} />
              ) : qrData.qrImageUrl ? (
                <img src={qrData.qrImageUrl} alt={qrData.title || "Protected QR"} />
              ) : (
                <LockOutlined />
              )}
              <div className="qrp-protected-lock"><LockOutlined /></div>
            </div>
            <span className="qrp-type-pill">{qrData.qrType || "QR"} QR</span>
            <Title level={3}>{qrData.title || "Protected QR"}</Title>
            {qrData.qrType === "Brand" && (
              <div className="qrp-protected-brand-note">
                <SafetyCertificateOutlined /> Secure brand link
              </div>
            )}
            {qrData.qrType === "Social" && (
              <div className="qrp-protected-brand-note">
                <ShareAltOutlined /> Protected social link
              </div>
            )}
            {qrData.qrType === "File" && (
              <div className="qrp-protected-brand-note">
                <DownloadOutlined /> Protected file
              </div>
            )}
            <Paragraph className="qrp-muted">This QR is password protected. Enter the password to continue.</Paragraph>
            <div className="qrp-password-form">
              <Input.Password
                size="large"
                placeholder="Enter password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onPressEnter={verifyPassword}
              />
              {passwordError && <div className="qrp-error">{passwordError}</div>}
              <Button type="primary" size="large" block loading={verifying} onClick={verifyPassword}>
                Unlock QR
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return <div className="qrp-page"><Card className="qrp-card">{renderContent()}</Card></div>;
};

export default QRPublic;
